import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  Badge,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  ThemeProvider,
  Tooltip
} from '@mui/material';
import { popoverTheme } from 'shared/styles/themes';
import PopoverState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { notificationService } from 'shared/services';
import { INotification } from 'features/notifications/types';

const NotificationsDropdown = () => {
  const [unreadNotifications, setUnreadNotifications] = useState<INotification[]>([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [updatedRead, setUpdateRead] = useState(false);

  const checkForNotifications = () => {
    notificationService
      .getUnread()
      .then((res) => {
        setUnreadNotifications(res.results);
        setUnreadNotificationsCount(res.count);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    checkForNotifications();
    const intervalId = setInterval(checkForNotifications, 30 * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    checkForNotifications();
  }, [updatedRead]);

  const handleNotificationClick = (value: INotification) => {
    if (!value.read) {
      notificationService.update(value.id, { read: true }).then((res) => {
        setUnreadNotifications((prevUnreadNotifications) =>
          prevUnreadNotifications.map((obj) => (obj.id === res.id ? res : obj))
        );
        setUnreadNotificationsCount((prevCount) => prevCount - 1);
        setUpdateRead((prevUpdatedRead) => !prevUpdatedRead);
      });
    }
  };

  const handleMarkAllAsRead = () => {
    if (unreadNotifications.length === 0) return;

    const promises = unreadNotifications.map((n) =>
      notificationService.update(n.id, { read: true })
    );

    setUnreadNotifications([]);
    setUnreadNotificationsCount(0);

    Promise.all(promises)
      .then(() => {
        setUpdateRead((prev) => !prev);
      })
      .catch((err) => {
        console.error('Errore nel segnare tutte le notifiche come lette:', err);
        checkForNotifications();
      });
  };

  return (
    <PopoverState variant="popover" popupId="notifications-popover">
      {(popupState) => (
        <div className="flex items-center">
          {/* Pulsante Campanella con Badge Contatore */}
          <Badge
            badgeContent={unreadNotificationsCount}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#5a6268',
                color: '#ffffff',
                fontSize: '10px',
                height: '16px',
                minWidth: '16px'
              }
            }}>
            <IconButton
              {...bindTrigger(popupState)}
              size="small"
              className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200/40 rounded-full transition-colors focus:outline-none">
              <NotificationsNoneIcon sx={{ fontSize: '22px', color: '#5a6268' }} />
            </IconButton>
          </Badge>

          {/* Pannello Elenco Notifiche a Scomparsa */}
          <ThemeProvider theme={popoverTheme}>
            <Popover
              {...bindPopover(popupState)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50/80 border-b border-slate-100 shrink-0 sticky top-0 z-10 backdrop-blur-sm">
                <span className="font-sans text-[13px] font-semibold text-[#1c1f23] tracking-tight">
                  Notifiche
                </span>
                {unreadNotifications.length > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllAsRead}
                    className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-[#1e3a8a] hover:bg-slate-200/50 rounded-md px-2 py-1 transition-all focus:outline-none">
                    <DoneAllIcon sx={{ fontSize: '15px' }} />
                    Segna come lette
                  </button>
                )}
              </div>

              <List className="py-0 divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {unreadNotifications && unreadNotifications.length > 0 ? (
                  unreadNotifications.map((notification) => (
                    <div
                      key={notification.notification.id}
                      className="flex justify-between items-center hover:bg-slate-50 transition-colors">
                      {/* Contenuto Testuale Notifica */}
                      <div className="flex-1 min-w-0 truncate">
                        <Tooltip
                          title={`${notification.notification.message}`}
                          placement="left-end">
                          <ListItemButton
                            component={NavLink}
                            to={`/tenant/notifications/view/${notification.id}`}
                            className="py-2.5 px-4"
                            onClick={() => popupState.close()}>
                            <ListItemText
                              className="text-left truncate"
                              primary={
                                notification.notification.title +
                                ': ' +
                                notification.notification.timestamp
                              }
                              primaryTypographyProps={{
                                className: 'text-[13px] font-medium text-slate-700 truncate'
                              }}
                            />
                          </ListItemButton>
                        </Tooltip>
                      </div>

                      {/* Pulsante di spunta per segnare come letto */}
                      <div className="shrink-0 pr-2">
                        <IconButton
                          size="small"
                          onClick={() => handleNotificationClick(notification)}
                          className="text-slate-400 hover:text-emerald-600 rounded-md p-1.5 transition-colors">
                          <CheckIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-6 text-center text-[13px] text-slate-400 font-sans select-none">
                    Nessuna nuova notifica
                  </div>
                )}
              </List>
            </Popover>
          </ThemeProvider>
        </div>
      )}
    </PopoverState>
  );
};

export default NotificationsDropdown;
